/**
 * Organization Finance Service
 * API calls for org-scoped finance management with RBAC
 */
import api from './api';

const BASE_URL = '/org/finance';

// ============================================
// SUBSCRIPTION MANAGEMENT
// ============================================

/**
 * Get current subscription status for the organization
 */
export const getSubscription = async () => {
  const response = await api.get(`${BASE_URL}/subscription`);
  return response.data;
};

/**
 * Start a free trial for the organization
 */
export const startTrial = async () => {
  const response = await api.post(`${BASE_URL}/subscription/trial`);
  return response.data;
};

/**
 * Upgrade subscription to a paid tier
 * @param {Object} upgradeData - { tier, billingCycle, paymentMethodId }
 */
export const upgradeSubscription = async (upgradeData) => {
  const response = await api.put(`${BASE_URL}/subscription/upgrade`, upgradeData);
  return response.data;
};

// ============================================
// FINANCE DEPARTMENT CONFIGURATION
// ============================================

/**
 * Get finance department configuration
 * Returns which department is the Finance authority and who the finance managers are
 */
export const getFinanceConfig = async () => {
  const response = await api.get(`${BASE_URL}/config`);
  return response.data;
};

/**
 * Configure which department is the Finance Department authority
 * @param {Object} configData - { financeDepartmentId, financeManagerIds }
 */
export const configureFinanceDepartment = async (configData) => {
  const response = await api.put(`${BASE_URL}/config`, configData);
  return response.data;
};

// ============================================
// DASHBOARD (Role-Based Views)
// ============================================

/**
 * Get role-based dashboard data
 * Finance users get admin view, others get department portal view
 */
export const getDashboard = async () => {
  const response = await api.get(`${BASE_URL}/dashboard`);
  return response.data;
};

// ============================================
// ORGANIZATION OVERVIEW
// ============================================

/**
 * Get organization finance overview
 */
export const getOverview = async () => {
  const response = await api.get(`${BASE_URL}/overview`);
  return response.data;
};

/**
 * Update organization finance overview
 * @param {Object} overviewData - Finance overview data to update
 */
export const updateOverview = async (overviewData) => {
  const response = await api.put(`${BASE_URL}/overview`, overviewData);
  return response.data;
};

// ============================================
// DEPARTMENT BUDGETS
// ============================================

/**
 * Get all department budgets (org_admin only)
 * @param {Object} params - { fiscalYear }
 */
export const getAllDepartmentBudgets = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.fiscalYear) queryParams.append('fiscalYear', params.fiscalYear);
  
  const url = `${BASE_URL}/departments${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Get budget for a specific department
 * @param {string} departmentId - Department ID
 * @param {Object} params - { fiscalYear }
 */
export const getDepartmentBudget = async (departmentId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.fiscalYear) queryParams.append('fiscalYear', params.fiscalYear);
  
  const url = `${BASE_URL}/departments/${departmentId}/budget${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Create or update department budget
 * @param {string} departmentId - Department ID
 * @param {Object} budgetData - Budget data
 */
export const upsertDepartmentBudget = async (departmentId, budgetData) => {
  const response = await api.put(`${BASE_URL}/departments/${departmentId}/budget`, budgetData);
  return response.data;
};

/**
 * Run budget allocation - recalculates spent amounts from actual expenses
 */
export const runBudgetAllocation = async () => {
  const response = await api.post(`${BASE_URL}/run-allocation`);
  return response.data;
};

// ============================================
// EXPENSES
// ============================================

/**
 * Get all organization expenses
 * @param {Object} params - { category, status, startDate, endDate, page, limit }
 */
export const getExpenses = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = `${BASE_URL}/expenses${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Get expenses for a department
 * @param {string} departmentId - Department ID
 * @param {Object} params - { status, category, startDate, endDate, page, limit }
 */
export const getDepartmentExpenses = async (departmentId, params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = `${BASE_URL}/departments/${departmentId}/expenses${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Create a new expense
 * @param {string} departmentId - Department ID
 * @param {Object} expenseData - Expense data
 */
export const createExpense = async (departmentId, expenseData) => {
  const response = await api.post(`${BASE_URL}/departments/${departmentId}/expenses`, expenseData);
  return response.data;
};

/**
 * Update an expense
 * @param {string} departmentId - Department ID
 * @param {string} expenseId - Expense ID
 * @param {Object} expenseData - Updated expense data
 */
export const updateExpense = async (departmentId, expenseId, expenseData) => {
  const response = await api.put(`${BASE_URL}/departments/${departmentId}/expenses/${expenseId}`, expenseData);
  return response.data;
};

/**
 * Delete an expense
 * @param {string} departmentId - Department ID
 * @param {string} expenseId - Expense ID
 */
export const deleteExpense = async (departmentId, expenseId) => {
  const response = await api.delete(`${BASE_URL}/departments/${departmentId}/expenses/${expenseId}`);
  return response.data;
};

/**
 * Approve a pending expense (Finance users only)
 * @param {string} departmentId - Department ID
 * @param {string} expenseId - Expense ID
 * @param {Object} data - { notes } optional approval notes
 */
export const approveExpense = async (departmentId, expenseId, data = {}) => {
  const response = await api.put(`${BASE_URL}/departments/${departmentId}/expenses/${expenseId}/approve`, data);
  return response.data;
};

/**
 * Reject a pending expense (Finance users only)
 * @param {string} departmentId - Department ID
 * @param {string} expenseId - Expense ID
 * @param {Object} data - { reason } required rejection reason
 */
export const rejectExpense = async (departmentId, expenseId, data) => {
  const response = await api.put(`${BASE_URL}/departments/${departmentId}/expenses/${expenseId}/reject`, data);
  return response.data;
};

/**
 * Upload a finance document (invoice, receipt, statement, etc.)
 * @param {File} file - The file to upload
 * @param {Object} data - { documentType, departmentId, description, amount, vendor }
 */
export const uploadFinanceDocument = async (file, data = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  
  const response = await api.post(`${BASE_URL}/upload-document`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// ============================================
// EMPLOYEE FINANCE
// ============================================

/**
 * Get employees for a department
 * @param {string} departmentId - Department ID
 * @param {Object} params - { status, search, page, limit }
 */
export const getDepartmentEmployees = async (departmentId, params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = `${BASE_URL}/departments/${departmentId}/employees${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Create a new employee
 * @param {string} departmentId - Department ID
 * @param {Object} employeeData - Employee data
 */
export const createEmployee = async (departmentId, employeeData) => {
  const response = await api.post(`${BASE_URL}/departments/${departmentId}/employees`, employeeData);
  return response.data;
};

/**
 * Update an employee
 * @param {string} departmentId - Department ID
 * @param {string} employeeId - Employee ID
 * @param {Object} employeeData - Updated employee data
 */
export const updateEmployee = async (departmentId, employeeId, employeeData) => {
  const response = await api.put(`${BASE_URL}/departments/${departmentId}/employees/${employeeId}`, employeeData);
  return response.data;
};

/**
 * Delete an employee
 * @param {string} departmentId - Department ID
 * @param {string} employeeId - Employee ID
 */
export const deleteEmployee = async (departmentId, employeeId) => {
  const response = await api.delete(`${BASE_URL}/departments/${departmentId}/employees/${employeeId}`);
  return response.data;
};

// ============================================
// BUDGET REQUESTS
// ============================================

/**
 * Get budget requests
 * @param {Object} params - { status, urgencyLevel, page, limit }
 */
export const getBudgetRequests = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = `${BASE_URL}/budget-requests${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Create a budget request
 * @param {Object} requestData - Budget request data
 */
export const createBudgetRequest = async (requestData) => {
  const response = await api.post(`${BASE_URL}/budget-requests`, requestData);
  return response.data;
};

/**
 * Approve a budget request (org_admin only)
 * @param {string} requestId - Request ID
 * @param {Object} approvalData - { approvedAmount, comments }
 */
export const approveBudgetRequest = async (requestId, approvalData) => {
  const response = await api.put(`${BASE_URL}/budget-requests/${requestId}/approve`, approvalData);
  return response.data;
};

/**
 * Reject a budget request (org_admin only)
 * @param {string} requestId - Request ID
 * @param {Object} rejectionData - { reason }
 */
export const rejectBudgetRequest = async (requestId, rejectionData) => {
  const response = await api.put(`${BASE_URL}/budget-requests/${requestId}/reject`, rejectionData);
  return response.data;
};

/**
 * Get a single budget request by ID
 * @param {string} requestId - Request ID
 */
export const getBudgetRequestById = async (requestId) => {
  const response = await api.get(`${BASE_URL}/budget-requests/${requestId}`);
  return response.data;
};

/**
 * Request clarification on a budget request (Finance users only)
 * @param {string} requestId - Request ID
 * @param {string} message - Clarification message
 */
export const requestClarification = async (requestId, message) => {
  const response = await api.put(`${BASE_URL}/budget-requests/${requestId}/clarify`, { message });
  return response.data;
};

/**
 * Add a comment to a budget request
 * @param {string} requestId - Request ID
 * @param {string} comment - Comment text
 * @param {boolean} isInternal - Whether comment is internal (finance-only)
 */
export const addRequestComment = async (requestId, comment, isInternal = false) => {
  const response = await api.post(`${BASE_URL}/budget-requests/${requestId}/comments`, {
    comment,
    isInternal
  });
  return response.data;
};

// ============================================
// ANALYTICS
// ============================================

/**
 * Get finance analytics
 * @param {Object} params - { startDate, endDate }
 */
export const getAnalytics = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  
  const url = `${BASE_URL}/analytics${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get scheduled reports
 */
export const getScheduledReports = async () => {
  const response = await api.get(`${BASE_URL}/scheduled-reports`);
  return response.data;
};

/**
 * Create a scheduled report
 * @param {Object} data - { frequency, email, reportTypes, format }
 */
export const createScheduledReport = async (data) => {
  const response = await api.post(`${BASE_URL}/scheduled-reports`, data);
  return response.data;
};

/**
 * Delete a scheduled report
 * @param {string} reportId - Report ID
 */
export const deleteScheduledReport = async (reportId) => {
  const response = await api.delete(`${BASE_URL}/scheduled-reports/${reportId}`);
  return response.data;
};

// ============================================
// UTILITIES
// ============================================

/**
 * Format currency with proper locale
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Calculate budget utilization percentage
 * @param {number} spent - Amount spent
 * @param {number} allocated - Amount allocated
 */
export const calculateUtilization = (spent, allocated) => {
  if (!allocated || allocated === 0) return 0;
  return Math.round((spent / allocated) * 100);
};

/**
 * Get utilization status color
 * @param {number} percentage - Utilization percentage
 */
export const getUtilizationColor = (percentage) => {
  if (percentage >= 95) return 'text-red-600 bg-red-100';
  if (percentage >= 80) return 'text-orange-600 bg-orange-100';
  if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
  return 'text-green-600 bg-green-100';
};

/**
 * Get subscription tier display info
 */
export const SUBSCRIPTION_TIERS = {
  free_trial: {
    name: 'Free Trial',
    color: 'bg-blue-100 text-blue-800',
    description: '14-day full access trial',
    limits: { maxEmployees: 50, maxDepartments: 5 }
  },
  starter: {
    name: 'Starter',
    color: 'bg-green-100 text-green-800',
    description: 'For small teams',
    limits: { maxEmployees: 100, maxDepartments: 10 },
    price: 49
  },
  professional: {
    name: 'Professional',
    color: 'bg-purple-100 text-purple-800',
    description: 'For growing organizations',
    limits: { maxEmployees: 500, maxDepartments: -1 },
    price: 149
  },
  enterprise: {
    name: 'Enterprise',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Unlimited everything',
    limits: { maxEmployees: -1, maxDepartments: -1 },
    price: 'Custom'
  }
};

/**
 * Expense categories with icons and colors
 */
export const EXPENSE_CATEGORIES = {
  salaries: { label: 'Salaries', color: 'bg-blue-500', icon: '💰' },
  software: { label: 'Software', color: 'bg-purple-500', icon: '💻' },
  hardware: { label: 'Hardware', color: 'bg-gray-500', icon: '🖥️' },
  travel: { label: 'Travel', color: 'bg-green-500', icon: '✈️' },
  training: { label: 'Training', color: 'bg-yellow-500', icon: '📚' },
  marketing: { label: 'Marketing', color: 'bg-pink-500', icon: '📢' },
  operations: { label: 'Operations', color: 'bg-orange-500', icon: '⚙️' },
  utilities: { label: 'Utilities', color: 'bg-teal-500', icon: '💡' },
  subscriptions: { label: 'Subscriptions', color: 'bg-indigo-500', icon: '📋' },
  bills: { label: 'Bills', color: 'bg-red-500', icon: '📄' },
  miscellaneous: { label: 'Miscellaneous', color: 'bg-gray-400', icon: '📦' }
};

/**
 * Budget request types
 */
export const REQUEST_TYPES = {
  hiring: { label: 'Hiring', color: 'bg-blue-500', icon: '👥' },
  assets: { label: 'Assets', color: 'bg-purple-500', icon: '🖥️' },
  operational: { label: 'Operational', color: 'bg-orange-500', icon: '⚙️' },
  project: { label: 'Project', color: 'bg-green-500', icon: '📋' },
  travel: { label: 'Travel', color: 'bg-teal-500', icon: '✈️' },
  training: { label: 'Training', color: 'bg-yellow-500', icon: '📚' },
  software: { label: 'Software', color: 'bg-indigo-500', icon: '💻' },
  other: { label: 'Other', color: 'bg-gray-500', icon: '📦' }
};

// ============================================
// EXCHANGE RATES
// ============================================

/**
 * Get latest exchange rates from database
 */
export const getExchangeRates = async () => {
  const response = await api.get(`${BASE_URL}/exchange-rates`);
  return response.data;
};

/**
 * Get all supported currencies with rates
 */
export const getAllCurrencies = async () => {
  const response = await api.get(`${BASE_URL}/exchange-rates/currencies`);
  return response.data;
};

/**
 * Convert currency amount
 * @param {number} amount - Amount to convert
 * @param {string} from - Source currency code
 * @param {string} to - Target currency code
 */
export const convertCurrency = async (amount, from, to) => {
  const response = await api.get(`${BASE_URL}/exchange-rates/convert`, {
    params: { amount, from, to }
  });
  return response.data;
};

/**
 * Refresh exchange rates (admin only)
 */
export const refreshExchangeRates = async () => {
  const response = await api.post(`${BASE_URL}/exchange-rates/refresh`);
  return response.data;
};

export default {
  // Subscription
  getSubscription,
  startTrial,
  upgradeSubscription,
  // Finance Department Configuration
  getFinanceConfig,
  configureFinanceDepartment,
  // Dashboard
  getDashboard,
  // Overview
  getOverview,
  updateOverview,
  // Department Budgets
  getAllDepartmentBudgets,
  getDepartmentBudget,
  upsertDepartmentBudget,
  runBudgetAllocation,
  // Expenses
  getExpenses,
  getDepartmentExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  approveExpense,
  rejectExpense,
  uploadFinanceDocument,
  // Employees
  getDepartmentEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  // Budget Requests
  getBudgetRequests,
  getBudgetRequestById,
  createBudgetRequest,
  approveBudgetRequest,
  rejectBudgetRequest,
  requestClarification,
  addRequestComment,
  // Analytics
  getAnalytics,
  // Scheduled Reports
  getScheduledReports,
  createScheduledReport,
  deleteScheduledReport,
  // Exchange Rates
  getExchangeRates,
  getAllCurrencies,
  convertCurrency,
  refreshExchangeRates,
  // Utilities
  formatCurrency,
  calculateUtilization,
  getUtilizationColor,
  SUBSCRIPTION_TIERS,
  EXPENSE_CATEGORIES,
  REQUEST_TYPES
};
